import type { ReactNode } from 'react'

import Stack from '@mui/material/Stack'
import type { SxProps } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'

import { STATUS_SUCCESS_TOKENS } from './statusTokens'

interface StatusMessageCardProps {
  /** Leading status icon (e.g. a check-circle). */
  icon: ReactNode
  /** Status message. */
  message: ReactNode
  /** Card surface color token. */
  bgcolor?: string
  /** Card border color token. */
  borderColor?: string
  /** Message text color token. */
  messageColor?: string
  sx?: SxProps
}

/**
 * Single-line status/success message card: a leading icon and a bold message on
 * a colored, bordered surface.
 *
 * Figma: the `All Categorized!` success state of Uncategorized Transactions V2
 * (`Card / Content / Slot`) — green card (`success.100` bg, `success.main`
 * border, radius 8), check-circle icon + bold message. Reusable for any
 * icon + message status/empty state.
 *
 * Scope is the Content section only; Header/Description are out of scope.
 */
const StatusMessageCard = ({
  bgcolor = STATUS_SUCCESS_TOKENS.bgcolor,
  borderColor = STATUS_SUCCESS_TOKENS.borderColor,
  icon,
  message,
  messageColor = STATUS_SUCCESS_TOKENS.textColor,
  sx,
}: StatusMessageCardProps) => (
  <Stack sx={{ px: 16, py: 16, width: '100%', ...sx }}>
    <Stack
      role="status"
      sx={{
        alignItems: 'center',
        bgcolor,
        border: '1px solid',
        borderColor,
        borderRadius: '8px',
        flexDirection: 'row',
        gap: 4,
        px: 16,
        py: 14,
        width: '100%',
      }}
    >
      <Stack sx={{ flexShrink: 0, px: 4, py: 6 }}>{icon}</Stack>
      <Text bold={true} color={messageColor} variant="body1">
        {message}
      </Text>
    </Stack>
  </Stack>
)

export default StatusMessageCard
